import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Star, Shield, Truck, PartyPopper, ChevronLeft, ChevronRight, Clock, Users, Ruler, CheckCircle2, Phone, MapPin, ArrowRight, Sparkles, Heart, Award, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Layout from '@/components/Layout';
import SEO from '@/components/SEO';
import { apiRequest, formatPrice } from '@/lib/utils';

// Fallback image for castles
const CASTLE_FALLBACK_IMAGE = "https://images.unsplash.com/photo-1527529482837-4698179dc6ce?auto=format&fit=crop&w=600&q=60";

// Real bouncy castle image - user provided
const HERO_CASTLE_IMAGE = "https://customer-assets.emergentagent.com/job_0538adf5-edcf-4e64-bcf3-6c7fb647f534/artifacts/ljqngu98_7FFD359A-4C35-4239-B5F1-DE77207E2FE0.PNG";
const BALLOONS_IMAGE = "https://images.unsplash.com/photo-1771368188458-302d6396c33f?w=600&q=80";

// Review source logos
const GoogleLogo = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const FacebookLogo = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24">
    <path fill="#1877F2" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

// Premium bouncy castle illustration - more detailed and colorful
const PremiumBouncyCastle = ({ className, variant = "default" }) => {
  const colors = {
    default: { main: "#E63954", secondary: "#FFD93D", accent: "#4ECDC4", trim: "#2E86AB" },
    blue: { main: "#2E86AB", secondary: "#FFD93D", accent: "#4ECDC4", trim: "#E63954" },
    rainbow: { main: "#FF6B6B", secondary: "#4ECDC4", accent: "#FFD93D", trim: "#A78BFA" }
  };
  const c = colors[variant] || colors.default;
  
  return (
    <svg viewBox="0 0 500 380" className={className} xmlns="http://www.w3.org/2000/svg">
      {/* Shadow */}
      <ellipse cx="250" cy="365" rx="180" ry="15" fill="rgba(0,0,0,0.1)" />
      
      {/* Main castle body */}
      <rect x="70" y="180" width="360" height="160" rx="25" fill={c.main} />
      <rect x="80" y="190" width="340" height="140" rx="20" fill={`${c.main}CC`} />
      
      {/* Bouncy surface highlights */}
      <ellipse cx="150" cy="280" rx="40" ry="12" fill="white" opacity="0.25" />
      <ellipse cx="250" cy="285" rx="45" ry="14" fill="white" opacity="0.25" />
      <ellipse cx="350" cy="280" rx="40" ry="12" fill="white" opacity="0.25" />
      
      {/* Left tower */}
      <rect x="80" y="90" width="80" height="110" rx="12" fill={c.accent} />
      <rect x="88" y="98" width="64" height="94" rx="8" fill={`${c.accent}CC`} />
      {/* Tower top */}
      <polygon points="120,90 70,50 170,50" fill={c.secondary} />
      <polygon points="120,90 80,55 160,55" fill={`${c.secondary}CC`} />
      {/* Flag */}
      <line x1="120" y1="50" x2="120" y2="15" stroke="#333" strokeWidth="4" strokeLinecap="round" />
      <polygon points="120,15 150,28 120,41" fill={c.main} />
      {/* Tower window */}
      <circle cx="120" cy="140" r="20" fill="white" opacity="0.9" />
      <circle cx="115" cy="135" r="6" fill={c.accent} opacity="0.3" />
      
      {/* Center tower (tallest) */}
      <rect x="195" y="60" width="110" height="140" rx="15" fill={c.secondary} />
      <rect x="205" y="70" width="90" height="120" rx="10" fill={`${c.secondary}CC`} />
      {/* Tower top */}
      <polygon points="250,60 180,10 320,10" fill={c.main} />
      <polygon points="250,60 195,20 305,20" fill={`${c.main}CC`} />
      {/* Flag */}
      <line x1="250" y1="10" x2="250" y2="-30" stroke="#333" strokeWidth="4" strokeLinecap="round" />
      <polygon points="250,-30 285,-15 250,0" fill={c.accent} />
      {/* Tower window */}
      <circle cx="250" cy="115" r="28" fill="white" opacity="0.9" />
      <circle cx="243" cy="108" r="8" fill={c.secondary} opacity="0.3" />
      
      {/* Right tower */}
      <rect x="340" y="90" width="80" height="110" rx="12" fill={c.trim} />
      <rect x="348" y="98" width="64" height="94" rx="8" fill={`${c.trim}CC`} />
      {/* Tower top */}
      <polygon points="380,90 330,50 430,50" fill={c.secondary} />
      <polygon points="380,90 340,55 420,55" fill={`${c.secondary}CC`} />
      {/* Flag */}
      <line x1="380" y1="50" x2="380" y2="15" stroke="#333" strokeWidth="4" strokeLinecap="round" />
      <polygon points="380,15 410,28 380,41" fill={c.accent} />
      {/* Tower window */}
      <circle cx="380" cy="140" r="20" fill="white" opacity="0.9" />
      <circle cx="375" cy="135" r="6" fill={c.trim} opacity="0.3" />
      
      {/* Castle entrance */}
      <rect x="200" y="230" width="100" height="110" rx="50" fill="rgba(0,0,0,0.35)" />
      <rect x="210" y="240" width="80" height="95" rx="40" fill="rgba(0,0,0,0.2)" />
      
      {/* Decorative trim between towers */}
      <rect x="150" y="170" width="50" height="25" rx="8" fill={c.secondary} />
      <rect x="300" y="170" width="50" height="25" rx="8" fill={c.secondary} />
      
      {/* Stars/sparkles */}
      <circle cx="100" cy="70" r="4" fill="white" opacity="0.8" />
      <circle cx="400" cy="75" r="3" fill="white" opacity="0.8" />
      <circle cx="250" cy="40" r="5" fill="white" opacity="0.8" />
    </svg>
  );
};

// Animated balloon cluster
const BalloonCluster = ({ className, style }) => (
  <div className={`absolute ${className}`} style={style}>
    <svg viewBox="0 0 120 180" className="w-full h-full">
      {/* Balloon 1 - Red */}
      <ellipse cx="35" cy="35" rx="28" ry="35" fill="#E63954" />
      <polygon points="35,70 30,78 40,78" fill="#E63954" opacity="0.9" />
      <ellipse cx="25" cy="25" rx="8" ry="12" fill="white" opacity="0.35" />
      
      {/* Balloon 2 - Yellow */}
      <ellipse cx="70" cy="45" rx="25" ry="32" fill="#FFD93D" />
      <polygon points="70,77 65,84 75,84" fill="#FFD93D" opacity="0.9" />
      <ellipse cx="62" cy="37" rx="7" ry="10" fill="white" opacity="0.35" />
      
      {/* Balloon 3 - Teal */}
      <ellipse cx="95" cy="30" rx="22" ry="28" fill="#4ECDC4" />
      <polygon points="95,58 91,64 99,64" fill="#4ECDC4" opacity="0.9" />
      <ellipse cx="88" cy="22" rx="6" ry="9" fill="white" opacity="0.35" />
      
      {/* Balloon 4 - Purple (behind) */}
      <ellipse cx="55" cy="60" rx="20" ry="26" fill="#A78BFA" opacity="0.9" />
      <polygon points="55,86 52,91 58,91" fill="#A78BFA" opacity="0.8" />
      <ellipse cx="49" cy="53" rx="5" ry="8" fill="white" opacity="0.3" />
      
      {/* Strings */}
      <path d="M35,78 Q38,120 35,160" stroke="#666" strokeWidth="1.5" fill="none" />
      <path d="M70,84 Q68,125 70,160" stroke="#666" strokeWidth="1.5" fill="none" />
      <path d="M95,64 Q92,110 95,160" stroke="#666" strokeWidth="1.5" fill="none" />
      <path d="M55,91 Q57,125 55,160" stroke="#666" strokeWidth="1.5" fill="none" />
      
      {/* Bow */}
      <circle cx="64" cy="165" r="8" fill="#E63954" />
    </svg>
  </div>
);

// Fluffy cloud component
const Cloud = ({ className, style }) => (
  <div className={`absolute ${className}`} style={style}>
    <svg viewBox="0 0 160 60" className="w-full h-full">
      <ellipse cx="40" cy="42" rx="35" ry="18" fill="white" />
      <ellipse cx="80" cy="38" rx="45" ry="22" fill="white" />
      <ellipse cx="120" cy="42" rx="35" ry="18" fill="white" />
      <ellipse cx="55" cy="25" rx="25" ry="15" fill="white" />
      <ellipse cx="100" cy="25" rx="28" ry="16" fill="white" />
    </svg>
  </div>
);

// Single floating balloon
const SingleBalloon = ({ color, className, style }) => (
  <div className={`absolute ${className}`} style={style}>
    <svg viewBox="0 0 50 80" className="w-full h-full">
      <ellipse cx="25" cy="28" rx="22" ry="28" fill={color} />
      <polygon points="25,56 20,62 30,62" fill={color} opacity="0.85" />
      <path d="M25,62 Q28,70 25,78" stroke="#777" strokeWidth="1.5" fill="none" />
      <ellipse cx="17" cy="18" rx="6" ry="9" fill="white" opacity="0.35" />
    </svg>
  </div>
);

// Confetti piece
const Confetti = ({ color, className, style, rotation = 0 }) => (
  <div 
    className={`absolute ${className}`} 
    style={{ 
      ...style, 
      transform: `rotate(${rotation}deg)`,
    }}
  >
    <div 
      className="w-3 h-3 rounded-sm"
      style={{ backgroundColor: color }}
    />
  </div>
);

export default function HomePage() {
  const [settings, setSettings] = useState(null);
  const [castles, setCastles] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    Promise.all([
      apiRequest('/settings'),
      apiRequest('/castles?featured_only=true'),
      apiRequest('/reviews'),
      apiRequest(`/availability?month=${currentMonth.getMonth() + 1}&year=${currentMonth.getFullYear()}`),
    ]).then(([settingsData, castlesData, reviewsData, availData]) => {
      setSettings(settingsData);
      setCastles(castlesData);
      setReviews(reviewsData);
      setAvailability(availData);
      setIsLoaded(true);
    }).catch(console.error);
  }, [currentMonth]);

  const getAvailabilityStatus = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    const entry = availability.find(a => a.date === dateStr);
    return entry?.status || 'available';
  };

  const renderCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPadding = firstDay.getDay();
    const days = [];

    for (let i = 0; i < startPadding; i++) {
      days.push(<div key={`pad-${i}`} className="h-7 sm:h-8" />);
    }

    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(year, month, day);
      const status = getAvailabilityStatus(date);
      const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));

      days.push(
        <div key={day} className={`h-7 sm:h-8 flex items-center justify-center text-xs sm:text-sm relative ${isPast ? 'text-slate-300' : 'text-slate-700'}`}>
          <span className="font-medium">{day}</span>
          {!isPast && (
            <span className={`absolute bottom-0 w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full ${
              status === 'available' ? 'bg-emerald-500' :
              status === 'limited' ? 'bg-amber-500' : 'bg-rose-500'
            }`} />
          )}
        </div>
      );
    }

    return days;
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  return (
    <Layout>
      {/* ========== PROMOTIONAL BANNER ========== */}
      {settings?.promotion_enabled && settings?.promotion_text && (
        <div className="bg-gradient-to-r from-[#E63954] via-[#FF6B6B] to-[#E63954] text-white py-2.5 overflow-hidden relative z-50" data-testid="promo-banner">
          <div className="animate-marquee whitespace-nowrap">
            <span className="mx-8 font-semibold text-sm md:text-base">
              ✨ {settings.promotion_text} ✨
            </span>
            <span className="mx-8 font-semibold text-sm md:text-base">
              ✨ {settings.promotion_text} ✨
            </span>
            <span className="mx-8 font-semibold text-sm md:text-base">
              ✨ {settings.promotion_text} ✨
            </span>
            <span className="mx-8 font-semibold text-sm md:text-base">
              ✨ {settings.promotion_text} ✨
            </span>
          </div>
        </div>
      )}

      {/* SEO Meta Tags */}
      <SEO 
        description="Premium bouncy castle hire for birthdays, parties and events in Auckland, New Zealand. Safe, clean, and professionally delivered inflatables. Book now!"
      />

      {/* ========== HERO SECTION ========== */}
      <section className="relative min-h-[550px] md:min-h-[650px] lg:min-h-[720px] overflow-hidden pb-16 md:pb-20" data-testid="hero-section">
        {/* Vibrant sky gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#47B5E6] via-[#6BC5ED] to-[#7DD889]" />
        
        {/* Decorative clouds */}
        <Cloud className="opacity-95 animate-float-slow hidden md:block" style={{ top: '3%', left: '2%', width: '180px' }} />
        <Cloud className="opacity-90 animate-float" style={{ top: '8%', right: '5%', width: '160px' }} />
        <Cloud className="opacity-85 animate-float-slow hidden lg:block" style={{ top: '18%', left: '55%', width: '140px' }} />
        <Cloud className="opacity-90 animate-float hidden md:block" style={{ top: '25%', left: '25%', width: '100px' }} />
        
        {/* Balloon clusters - hidden on small mobile */}
        <BalloonCluster className="animate-float-balloon w-16 h-24 md:w-28 md:h-40 hidden sm:block" style={{ top: '5%', left: '5%' }} />
        <BalloonCluster className="animate-float-slow w-14 h-20 md:w-24 md:h-36 hidden sm:block" style={{ top: '3%', right: '8%' }} />
        
        {/* Individual floating balloons - fewer on mobile */}
        <SingleBalloon color="#E63954" className="animate-float-balloon w-8 h-12 md:w-12 md:h-20 hidden md:block" style={{ top: '15%', left: '18%' }} />
        <SingleBalloon color="#FFD93D" className="animate-float-slow w-8 h-13 md:w-10 md:h-16" style={{ top: '12%', right: '10%' }} />
        <SingleBalloon color="#4ECDC4" className="animate-float-fast w-7 h-11 md:w-11 md:h-18 hidden lg:block" style={{ top: '10%', right: '35%' }} />
        <SingleBalloon color="#A78BFA" className="animate-float w-6 h-10 md:w-9 md:h-14 hidden md:block" style={{ top: '28%', left: '8%' }} />
        
        {/* Scattered confetti - hidden on mobile */}
        <Confetti color="#E63954" className="animate-float-slow hidden md:block" style={{ top: '12%', left: '30%' }} rotation={15} />
        <Confetti color="#FFD93D" className="animate-float hidden lg:block" style={{ top: '20%', left: '70%' }} rotation={-20} />
        <Confetti color="#4ECDC4" className="animate-float-fast hidden md:block" style={{ top: '8%', left: '45%' }} rotation={30} />
        <Confetti color="#A78BFA" className="animate-float-slow hidden lg:block" style={{ top: '25%', right: '15%' }} rotation={-10} />
        
        {/* Main hero content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 md:pt-10 lg:pt-14 z-10">
          <div className="grid lg:grid-cols-2 gap-4 md:gap-8 lg:gap-12 items-end">
            {/* Left - Text content */}
            <div className="text-center lg:text-left order-2 lg:order-1">
              {/* Trust badge */}
              <div className="inline-flex items-center gap-2 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1.5 md:px-4 md:py-2 mb-4 md:mb-6 shadow-lg animate-bounce-gentle">
                <Award className="w-4 h-4 md:w-5 md:h-5 text-amber-500" />
                <span className="text-xs md:text-sm font-semibold text-slate-700">Auckland's Trusted Bouncy Castle Hire</span>
              </div>
              
              <h1 className="font-outfit font-black text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl text-slate-900 leading-[1.05] mb-1 md:mb-2 drop-shadow-sm">
                Make Your Party
              </h1>
              <h2 className="font-outfit font-black text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-[1.05] mb-3 md:mb-5">
                <span className="bg-gradient-to-r from-[#E63954] via-[#FF6B6B] to-[#E63954] bg-clip-text text-transparent italic">
                  Unforgettable!
                </span>
              </h2>
              <p className="font-nunito text-base md:text-lg lg:text-xl text-slate-700 mb-6 md:mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                Premium bouncy castle hire for birthdays, events & celebrations. 
                <span className="font-semibold text-slate-800"> Safe, clean & professionally delivered!</span>
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center lg:justify-start">
                <Link to="/castles">
                  <Button 
                    className="w-full sm:w-auto rounded-full bg-gradient-to-r from-[#E63954] to-[#D62E48] hover:from-[#D62E48] hover:to-[#C0263E] text-white font-outfit font-bold px-6 md:px-8 py-5 md:py-6 text-base md:text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 hover:scale-105"
                    data-testid="view-castles-btn"
                  >
                    <Sparkles className="mr-2 w-4 h-4 md:w-5 md:h-5" />
                    View Our Castles
                  </Button>
                </Link>
                <Link to="/booking">
                  <Button 
                    variant="outline"
                    className="w-full sm:w-auto rounded-full border-3 border-slate-800 bg-white/95 text-slate-800 font-outfit font-bold px-6 md:px-8 py-5 md:py-6 text-base md:text-lg hover:bg-white hover:scale-105 transition-all duration-300 shadow-lg"
                    data-testid="get-quote-btn"
                  >
                    <Calendar className="mr-2 w-4 h-4 md:w-5 md:h-5" />
                    Book Now
                  </Button>
                </Link>
              </div>
              
              {/* Quick stats */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-6 mt-8 text-slate-600">
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <span className="text-sm font-semibold">5.0 Rating</span>
                </div>
                <div className="flex items-center gap-2">
                  <Heart className="w-4 h-4 fill-rose-500 text-rose-500" />
                  <span className="text-sm font-semibold">Happy Families</span>
                </div>
              </div>
            </div>

            {/* Right - Castle image */}
            <div className="relative order-1 lg:order-2 flex justify-center items-end">
              <div className="relative w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg">
                {/* Rectangular image with soft blended edges on all sides */}
                <div className="relative">
                  <img 
                    src={HERO_CASTLE_IMAGE}
                    alt="Colorful bouncy castle with slides"
                    className="w-full h-auto object-contain rounded-2xl"
                    style={{ 
                      filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.1))',
                      maskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%), linear-gradient(to bottom, transparent 0%, black 8%, black 92%, transparent 100%)',
                      WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%), linear-gradient(to bottom, transparent 0%, black 8%, black 92%, transparent 100%)',
                      maskComposite: 'intersect',
                      WebkitMaskComposite: 'source-in'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Grass and ground transition */}
        <div className="absolute bottom-0 left-0 right-0">
          {/* Vibrant grass */}
          <div className="h-12 md:h-16 bg-gradient-to-b from-[#5CBD6A] to-[#3DA54B]" />
        </div>
      </section>

      {/* ========== TRUST INDICATORS ========== */}
      <section className="bg-white py-8 md:py-10 border-b border-slate-100 shadow-sm relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: PartyPopper, color: 'rose', title: 'Party Perfect', desc: 'Ideal for birthdays & events' },
              { icon: Shield, color: 'emerald', title: 'Safe & Insured', desc: 'Fully sanitized equipment' },
              { icon: Truck, color: 'amber', title: 'Professional Delivery', desc: 'Setup & pack-down included' },
            ].map((item, i) => (
              <div key={i} className="flex items-center justify-center gap-4 group">
                <div className={`w-16 h-16 rounded-2xl bg-${item.color}-100 flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform duration-300`}>
                  <item.icon className={`w-8 h-8 text-${item.color}-600`} />
                </div>
                <div>
                  <p className={`font-outfit font-bold text-lg text-${item.color}-600`}>{item.title}</p>
                  <p className="text-sm text-slate-600 font-nunito">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== FEATURED CASTLES + CALENDAR ========== */}
      <section className="relative py-16 md:py-24 overflow-hidden" data-testid="popular-section">
        {/* Sky gradient background */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#E3F4FB] via-[#EDF8FC] to-[#F0F9FD]" />
        
        {/* Decorative elements */}
        <SingleBalloon color="#E63954" className="animate-float-slow w-10 h-16 hidden md:block" style={{ top: '15%', left: '3%' }} />
        <SingleBalloon color="#4ECDC4" className="animate-float-balloon w-8 h-13 hidden md:block" style={{ top: '45%', right: '3%' }} />
        <Cloud className="opacity-60 animate-float" style={{ top: '5%', right: '10%', width: '100px' }} />
        
        {/* Grass at bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#5CBD6A]/30 via-[#7DD889]/20 to-transparent" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
          <div className="grid lg:grid-cols-3 gap-10">
            {/* Featured Castles - 2 columns */}
            <div className="lg:col-span-2">
              <div className="mb-10 text-center lg:text-left">
                <span className="inline-block bg-rose-100 text-rose-600 text-sm font-bold px-4 py-1.5 rounded-full mb-4">
                  MOST POPULAR
                </span>
                <h2 className="font-outfit font-bold text-3xl md:text-4xl text-slate-900 mb-3">
                  Our Featured Bouncy Castles
                </h2>
                <p className="text-slate-600 font-nunito text-lg">Choose from our range of premium inflatables</p>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {!isLoaded ? (
                  // Loading skeleton - shows immediately while data loads
                  [1, 2, 3].map((i) => (
                    <Card key={i} className="rounded-3xl overflow-hidden border-0 shadow-xl bg-white">
                      <div className="h-44 bg-slate-200 animate-pulse" />
                      <CardContent className="p-5">
                        <div className="h-5 bg-slate-200 rounded animate-pulse mb-3 w-3/4" />
                        <div className="flex gap-3 mb-4">
                          <div className="h-6 w-20 bg-slate-200 rounded-full animate-pulse" />
                          <div className="h-6 w-20 bg-slate-200 rounded-full animate-pulse" />
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="h-6 w-16 bg-slate-200 rounded animate-pulse" />
                          <div className="h-9 w-24 bg-slate-200 rounded-full animate-pulse" />
                        </div>
                      </CardContent>
                    </Card>
                  ))
                ) : castles.length > 0 ? castles.slice(0, 3).map((castle, idx) => (
                  <Card 
                    key={castle.id} 
                    className="rounded-3xl overflow-hidden border-0 shadow-xl bg-white group hover:-translate-y-2 transition-all duration-300" 
                    data-testid={`castle-card-${castle.id}`}
                  >
                    {/* Castle image */}
                    <div className="relative h-44 bg-slate-100 overflow-hidden">
                      <img 
                        src={castle.main_image || castle.images?.[0] || CASTLE_FALLBACK_IMAGE}
                        alt={castle.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                        onError={(e) => { e.target.src = CASTLE_FALLBACK_IMAGE; }}
                      />
                      
                      {castle.tags?.includes('Most Popular') && (
                        <span className="absolute top-3 right-3 bg-gradient-to-r from-rose-500 to-rose-600 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg">
                          Popular
                        </span>
                      )}
                      
                      {castle.featured && !castle.tags?.includes('Most Popular') && (
                        <span className="absolute top-3 right-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg">
                          Featured
                        </span>
                      )}
                    </div>
                    
                    <CardContent className="p-5">
                      <h3 className="font-outfit font-bold text-lg text-slate-900 mb-2 group-hover:text-rose-600 transition-colors">{castle.name}</h3>
                      
                      <div className="flex items-center gap-4 text-xs text-slate-500 mb-4">
                        <span className="flex items-center gap-1.5 bg-slate-100 px-2 py-1 rounded-full">
                          <Users className="w-3 h-3" /> {castle.age_range || '3-12 yrs'}
                        </span>
                        <span className="flex items-center gap-1.5 bg-slate-100 px-2 py-1 rounded-full">
                          <Ruler className="w-3 h-3" /> {castle.length || 4}x{castle.width || 4}m
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-outfit font-bold text-xl text-sky-600">{formatPrice(castle.price)}</p>
                        </div>
                        <Link to={`/castles/${castle.id}`}>
                          <Button size="sm" className="rounded-full bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 text-white font-outfit font-semibold text-xs px-5 shadow-md hover:shadow-lg transition-all">
                            View Details
                          </Button>
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                )) : (
                  // No castles available message
                  <div className="col-span-full text-center py-8">
                    <p className="text-slate-500 font-nunito">No featured castles available. Check back soon!</p>
                  </div>
                )}
              </div>

              <div className="mt-10 text-center">
                <Link to="/castles">
                  <Button className="rounded-full bg-gradient-to-r from-[#1E5F8A] to-[#2980B9] hover:from-[#164A6D] hover:to-[#1E5F8A] text-white font-outfit font-bold px-10 py-4 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
                    View All Bouncy Castles <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Availability Calendar */}
            <div className="lg:col-span-1">
              <Card className="rounded-3xl border-0 shadow-2xl bg-white overflow-hidden sticky top-8" data-testid="availability-calendar">
                <div className="bg-gradient-to-r from-[#1E5F8A] via-[#2477A4] to-[#2980B9] p-5">
                  <h3 className="font-outfit font-bold text-xl text-white text-center flex items-center justify-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Check Availability
                  </h3>
                </div>
                
                <CardContent className="p-5">
                  {/* Legend */}
                  <div className="flex items-center justify-center gap-4 mb-5 text-xs">
                    <div className="flex items-center gap-1.5">
                      <span className="w-3 h-3 rounded-full bg-emerald-500 shadow-sm" />
                      <span className="text-slate-600 font-medium">Available</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="w-3 h-3 rounded-full bg-amber-500 shadow-sm" />
                      <span className="text-slate-600 font-medium">Limited</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="w-3 h-3 rounded-full bg-rose-500 shadow-sm" />
                      <span className="text-slate-600 font-medium">Booked</span>
                    </div>
                  </div>

                  {/* Month Navigation */}
                  <div className="flex items-center justify-between mb-5 bg-slate-100 rounded-full px-3 py-2">
                    <button 
                      onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                      className="p-2 hover:bg-white rounded-full transition-all hover:shadow-md"
                      aria-label="Previous month"
                    >
                      <ChevronLeft className="w-5 h-5 text-slate-600" />
                    </button>
                    <span className="font-outfit font-bold text-slate-800">
                      {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                    </span>
                    <button 
                      onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                      className="p-2 hover:bg-white rounded-full transition-all hover:shadow-md"
                      aria-label="Next month"
                    >
                      <ChevronRight className="w-5 h-5 text-slate-600" />
                    </button>
                  </div>

                  {/* Calendar Grid */}
                  <div className="grid grid-cols-7 gap-1 text-center">
                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                      <div key={i} className="text-xs font-bold text-slate-400 pb-2">{day}</div>
                    ))}
                    {renderCalendar()}
                  </div>
                  
                  <div className="mt-6 pt-5 border-t border-slate-100">
                    <Link to="/booking">
                      <Button className="w-full rounded-full bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 text-white font-outfit font-bold py-5 shadow-lg hover:shadow-xl transition-all">
                        Request a Booking
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* ========== WHY CHOOSE US ========== */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="inline-block bg-sky-100 text-sky-600 text-sm font-bold px-4 py-1.5 rounded-full mb-4">
              WHY US
            </span>
            <h2 className="font-outfit font-bold text-3xl md:text-4xl text-slate-900 mb-4">
              Why Choose Master Castles?
            </h2>
            <p className="text-slate-600 font-nunito text-lg max-w-2xl mx-auto">
              Auckland's most trusted bouncy castle hire specialists
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Shield,
                gradient: 'from-emerald-500 to-teal-500',
                bgColor: 'bg-emerald-50',
                title: 'Safe & Clean',
                points: ['Fully insured for peace of mind', 'Sanitized after every use', 'Regular safety inspections', 'NZ safety standards compliant']
              },
              {
                icon: Clock,
                gradient: 'from-sky-500 to-blue-500',
                bgColor: 'bg-sky-50',
                title: 'Reliable Service',
                points: ['On-time delivery guaranteed', 'Professional setup & pack down', 'Friendly, helpful team', 'Clear communication always']
              },
              {
                icon: PartyPopper,
                gradient: 'from-rose-500 to-pink-500',
                bgColor: 'bg-rose-50',
                title: 'Great Value',
                points: ['Competitive pricing', 'Auckland-wide delivery', 'Wide range of castles', 'No hidden fees']
              }
            ].map((item, i) => (
              <Card key={i} className={`rounded-3xl border-0 shadow-xl ${item.bgColor} p-8 hover:shadow-2xl hover:-translate-y-2 transition-all duration-300`}>
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${item.gradient} flex items-center justify-center mb-6 shadow-lg`}>
                  <item.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-outfit font-bold text-xl text-slate-900 mb-5">{item.title}</h3>
                <ul className="space-y-3">
                  {item.points.map((point, j) => (
                    <li key={j} className="flex items-start gap-3 text-slate-600 font-nunito">
                      <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ========== HOW IT WORKS ========== */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#EDF8FC] via-[#E3F4FB] to-[#D9F0FA]" />
        
        {/* Decorative elements */}
        <Cloud className="opacity-50 animate-float-slow" style={{ top: '10%', left: '5%', width: '120px' }} />
        <Cloud className="opacity-40 animate-float" style={{ bottom: '15%', right: '8%', width: '100px' }} />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-amber-100 text-amber-600 text-sm font-bold px-4 py-1.5 rounded-full mb-4">
              EASY PROCESS
            </span>
            <h2 className="font-outfit font-bold text-3xl md:text-4xl text-slate-900 mb-4">
              Book in 3 Simple Steps
            </h2>
            <p className="text-slate-600 font-nunito text-lg">Fast, easy booking for your next event</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { step: '1', title: 'Choose Your Castle', desc: 'Browse our selection and find the perfect bouncy castle for your event', icon: Sparkles },
              { step: '2', title: 'Submit Request', desc: 'Fill out our simple booking form with your event details', icon: Calendar },
              { step: '3', title: 'We Deliver & Setup', desc: "We bring the fun to you! Professional delivery and setup included", icon: Truck },
            ].map((item, i) => (
              <div key={i} className="text-center group">
                <div className="relative inline-block mb-6">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-rose-500 to-rose-600 text-white font-outfit font-black text-3xl flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform duration-300">
                    {item.step}
                  </div>
                  <div className="absolute -bottom-2 -right-2 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-rose-500" />
                  </div>
                </div>
                <h3 className="font-outfit font-bold text-xl text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600 font-nunito leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/booking">
              <Button className="rounded-full bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 text-white font-outfit font-bold px-12 py-6 text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
                Start Your Booking <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ========== REVIEWS ========== */}
      <section className="py-16 md:py-24 bg-white" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="inline-block bg-amber-100 text-amber-600 text-sm font-bold px-4 py-1.5 rounded-full mb-4">
              TESTIMONIALS
            </span>
            <h2 className="font-outfit font-bold text-3xl md:text-4xl text-slate-900 mb-4">
              What Our Customers Say
            </h2>
            <p className="text-slate-600 font-nunito text-lg">Trusted by hundreds of Auckland families</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {reviews.length > 0 ? reviews.slice(0, 3).map((review) => (
              <Card key={review.id} className="rounded-3xl border-2 border-amber-200/50 bg-gradient-to-b from-amber-50/80 to-white p-7 shadow-lg hover:shadow-xl transition-all duration-300" data-testid={`review-card-${review.id}`}>
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className={`w-5 h-5 ${i < review.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`} />
                    ))}
                  </div>
                  {/* Source logo */}
                  {review.source === 'facebook' ? <FacebookLogo className="w-6 h-6" /> : <GoogleLogo className="w-6 h-6" />}
                </div>
                <p className="text-slate-700 font-nunito mb-5 italic leading-relaxed">"{review.review_text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rose-400 to-rose-500 flex items-center justify-center text-white font-bold text-sm">
                    {review.author_name?.charAt(0) || 'A'}
                  </div>
                  <p className="text-slate-900 font-outfit font-bold">{review.author_name}</p>
                </div>
              </Card>
            )) : (
              [
                { name: "Sarah T.", text: "Fantastic service! The kids had a blast and the castle was spotless. Will definitely book again!", initial: 'S', source: 'google' },
                { name: "David M.", text: "Very professional and clean setup. On-time delivery and friendly team. Highly recommend!", initial: 'D', source: 'google' },
                { name: "Emma L.", text: "Best party ever! The children absolutely loved the bouncy castle. Excellent service.", initial: 'E', source: 'facebook' },
              ].map((review, i) => (
                <Card key={i} className="rounded-3xl border-2 border-amber-200/50 bg-gradient-to-b from-amber-50/80 to-white p-7 shadow-lg">
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, j) => (
                        <Star key={j} className="w-5 h-5 fill-amber-400 text-amber-400" />
                      ))}
                    </div>
                    {review.source === 'facebook' ? <FacebookLogo className="w-6 h-6" /> : <GoogleLogo className="w-6 h-6" />}
                  </div>
                  <p className="text-slate-700 font-nunito mb-5 italic leading-relaxed">"{review.text}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rose-400 to-rose-500 flex items-center justify-center text-white font-bold text-sm">
                      {review.initial}
                    </div>
                    <p className="text-slate-900 font-outfit font-bold">{review.name}</p>
                  </div>
                </Card>
              ))
            )}
          </div>

          <div className="text-center mt-10">
            <a href="https://maps.app.goo.gl/aggriNwGpiUmbveW7?g_st=ic" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="rounded-full border-2 border-slate-300 font-outfit font-semibold px-8 py-3 hover:border-sky-500 hover:text-sky-600 hover:bg-sky-50 transition-all duration-300">
                See All Reviews <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* ========== SERVICE AREAS ========== */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1E5F8A] via-[#1A5276] to-[#154360]" />
        
        {/* Decorative balloons */}
        <SingleBalloon color="#FFD93D" className="animate-float-balloon w-10 h-16 opacity-50" style={{ top: '10%', left: '5%' }} />
        <SingleBalloon color="#E63954" className="animate-float-slow w-8 h-13 opacity-50" style={{ bottom: '20%', right: '8%' }} />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <span className="inline-block bg-white/20 text-white text-sm font-bold px-4 py-1.5 rounded-full mb-5">
                SERVICE AREA
              </span>
              <h2 className="font-outfit font-bold text-3xl md:text-4xl mb-5">
                Delivering Fun Across Auckland
              </h2>
              <p className="font-nunito text-lg text-sky-100 mb-8 leading-relaxed">
                We deliver and set up bouncy castles throughout the entire Auckland region. Delivery charges vary by area.
              </p>
              <ul className="grid grid-cols-2 gap-4 mb-10">
                {['West Auckland', 'South Auckland', 'North Shore', 'Central Auckland', 'East Auckland', 'Hibiscus Coast'].map((area, i) => (
                  <li key={i} className="flex items-center gap-3 font-nunito">
                    <div className="w-6 h-6 rounded-full bg-sky-400/30 flex items-center justify-center">
                      <MapPin className="w-3.5 h-3.5 text-sky-300" />
                    </div>
                    <span>{area}</span>
                  </li>
                ))}
              </ul>
              <Link to="/contact">
                <Button className="rounded-full bg-white text-[#1E5F8A] font-outfit font-bold px-8 py-4 hover:bg-sky-50 hover:scale-105 transition-all duration-300 shadow-xl">
                  <Phone className="mr-2 w-5 h-5" />
                  Contact Us For Pricing
                </Button>
              </Link>
            </div>
            
            <div className="flex justify-center">
              <div className="relative">
                {/* Auckland map visualization */}
                <div className="w-72 h-72 md:w-80 md:h-80 rounded-full bg-gradient-to-br from-sky-400/40 to-sky-600/40 flex items-center justify-center shadow-2xl border-4 border-white/20">
                  <div className="text-center text-white">
                    <MapPin className="w-16 h-16 mx-auto mb-3 drop-shadow-lg" />
                    <p className="font-outfit font-bold text-2xl">Auckland Region</p>
                    <p className="font-nunito text-sky-100 mt-1">Professional delivery & setup</p>
                  </div>
                </div>
                
                {/* Decorative elements around map */}
                <BalloonCluster className="animate-float-balloon w-16 h-24" style={{ top: '-15%', left: '-10%' }} />
                <SingleBalloon color="#4ECDC4" className="animate-float-slow w-8 h-13" style={{ bottom: '5%', right: '-5%' }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== FINAL CTA ========== */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-[#E63954] via-[#E84363] to-[#D62E48] relative overflow-hidden">
        {/* Decorative elements */}
        <BalloonCluster className="animate-float-balloon w-20 h-28 opacity-40" style={{ top: '5%', left: '3%' }} />
        <SingleBalloon color="#FFD93D" className="animate-float-slow w-12 h-20 opacity-50" style={{ bottom: '15%', right: '5%' }} />
        <SingleBalloon color="#4ECDC4" className="animate-float w-10 h-16 opacity-40" style={{ top: '20%', right: '20%' }} />
        
        {/* Confetti */}
        <Confetti color="#FFD93D" className="animate-float" style={{ top: '10%', left: '15%' }} rotation={25} />
        <Confetti color="#4ECDC4" className="animate-float-slow" style={{ top: '30%', right: '25%' }} rotation={-15} />
        <Confetti color="#A78BFA" className="animate-float-fast" style={{ bottom: '20%', left: '30%' }} rotation={40} />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
          <h2 className="font-outfit font-black text-3xl md:text-5xl text-white mb-5 drop-shadow-lg">
            Ready to Make Your Party Unforgettable?
          </h2>
          <p className="text-white/90 font-nunito text-lg md:text-xl mb-10 max-w-2xl mx-auto leading-relaxed">
            Book your bouncy castle today and create magical memories for your children!
          </p>
          <div className="flex flex-col sm:flex-row gap-5 justify-center">
            <Link to="/booking">
              <Button className="w-full sm:w-auto rounded-full bg-white text-rose-600 font-outfit font-bold px-12 py-6 text-lg shadow-2xl hover:shadow-3xl transition-all duration-300 hover:-translate-y-2 hover:scale-105 hover:bg-sky-50">
                <Sparkles className="mr-2 w-5 h-5" />
                Request a Booking
              </Button>
            </Link>
            <Link to="/castles">
              <Button variant="outline" className="w-full sm:w-auto rounded-full border-3 border-white text-white font-outfit font-bold px-12 py-6 text-lg hover:bg-white/15 transition-all duration-300">
                View Our Castles
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
